import {
  milisecondsToSeconds,
  minutesToMiliseconds,
  nowMilisecondsTimestamp,
  secondsToMiliseconds,
  yearToSeconds,
} from './time'
import { fitsWithin } from './numbers'
import { BundlePeriod } from '@/api/types/user'

/**
 * Concatenate the "now" and an arbitrary numer of minutes and return an ETA hour
 * @param createdTimestampMs A "starting point" timestamp
 * @param processingTimeMinutes Amount of computing time necessary
 * @returns a neatly-formatted ETA time
 */
export const getEtaString = (createdTimestampMs: number, processingTimeMinutes: number): string => {
  const newDate = new Date(createdTimestampMs + minutesToMiliseconds(processingTimeMinutes))
  const hours = newDate.getHours()
  const minutes = newDate.getMinutes()
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * The Date API used throughout here theoretically "knows" how do locale string look,
 * but am not sure about their support. On https://caniuse.com/?search=toLocaleString
 * support for "options" and "locales" parameters seems pretty good;
 * let's return to it once we do different language versions?
 *
 * @param number a month index
 * @returns month's full name string
 */
export const getMonthFullName = (number: number): string => {
  const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const monthName = MONTH_NAMES[number]
  if (!monthName) {
    throw new RangeError('The number provided does not map to month full names array.')
  }
  return monthName
}

/**
 * How many days does the month have in total?
 *
 * @param month a month which we find interest in
 * @param year a year of the said month
 * @returns a number between 28 and 31
 */
export const getTotalDaysInMonth = (month: number, year: number): number => {
  // passing zero to day parameter in .getDate() returns the max amount of days of THE PREVIOUS MONTH
  const monthWeHaveInterestIn = month + 1
  return new Date(year, monthWeHaveInterestIn, 0).getDate()
}

/**
 * @param timestamp a miliseconds timestamp
 * @returns a nicely formatted date
 */
export const milisecondsToDateString = (timestamp: number | null = null): string => {
  if (timestamp === null) {
    throw new TypeError('The timestamp was not provided.')
  }
  const timestampDate = new Date(timestamp)
  const monthName = getMonthFullName(timestampDate.getMonth())
  const dayNumber = timestampDate.getDate()
  const year = timestampDate.getFullYear()
  return `${monthName} ${dayNumber}, ${year}`
}

/**
 * Get a timestamp of same day next month OR of its last day if the number exceeds amount of days available the month.
 *
 * If you just swap the month, date API will try to add the current date to the new one, occassionally causing an additional month flip in case the date does not fit.
 * We want to prevent this behavior and instead provide the last day of the upcoming month.
 * @param timestamp miliseconds timestamp as base
 *
 * @returns a new timestamp of the same day next month or of its max days.
 */
export const getSameDayOrMaxOfNextMonthTimestamp = (timestamp: number): number => {
  const timestampDate = new Date(timestamp)
  const currentMonthNumber = timestampDate.getMonth()
  const currentYear = timestampDate.getFullYear()

  const currentDate = timestampDate.getDate()

  const nextMonthNumber = currentMonthNumber + 1
  const nextMonthNumberOfDays = getTotalDaysInMonth(nextMonthNumber, currentYear)
  const doesTheDateFit = fitsWithin(currentDate, nextMonthNumberOfDays)

  if (doesTheDateFit) {
    timestampDate.setMonth(timestampDate.getMonth() + 1)
  } else {
    timestampDate.setMonth(timestampDate.getMonth() + 1, nextMonthNumberOfDays) // second argument is a date
  }

  return timestampDate.getTime()
}

// it looks a lot like shared/time, but is not used by cloud functions, so putting it here
/**
 * Get the number of days that fit in a given number of miliseconds
 * @param timestamp miliseconds that should fill the days
 * @returns number of days
 */
const milisecondsToDays = (timestamp: number): number => {
  const MILISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24
  return timestamp / MILISECONDS_IN_A_DAY
}

/**
 * Get a float representing days in a given amount of timestamps.
 * @param t1 first timestamp
 * @param t2 second timestamp
 * @returns
 */
export const getDaysBetweenTimestamps = (t1: number, t2: number): number => {
  const timestampDiff = Math.abs(t2 - t1)
  return milisecondsToDays(timestampDiff)
}
/**
 * Get number of days between two timestamps, useful when counting days remaining
 * @param t1 first timestamp
 * @param t2 second timestamp
 * @returns
 */
export const getFullDaysBetweenTimestamps = (t1: number, t2: number): number => {
  const days = getDaysBetweenTimestamps(t2, t1)
  return Math.floor(days)
}

/**
 * Get number of days between two timestamps rouded up, useful when counting actual price for additional workstation.
 * @param t1 first timestamp
 * @param t2 second timestamp
 * @returns
 */
export const getDaysRoudedUpBetweenTimestamps = (t1: number, t2: number): number => {
  const days = getDaysBetweenTimestamps(t2, t1)
  return Math.ceil(days)
}

export type BillingPeriod = {
  start: string
  end: string
  trialEnd: number
  isTrialOngoing: boolean
}

// shallow helper, not tested
export const getBillingPeriodEndStringFromSecondsTimestamp = (created: number): string => {
  const createdMiliseconds = secondsToMiliseconds(created)
  return milisecondsToDateString(getSameDayOrMaxOfNextMonthTimestamp(createdMiliseconds))
}

/**
 * Return an object full of useful properties to display dates related to billing period
 *
 * @param created date IN SECONDS
 * @param trial_end possible null
 * @param now timestamp in MILISECONDS
 * @param bundle_period 'month' | 'year' or undefind( defaults to month in legacy plans) depending on the plan
 * @returns the object
 */
export const getBillingPeriodObject = (
  created: number,
  trial_end: number,
  bundle_period: BundlePeriod,
): BillingPeriod => {
  const createdMiliseconds = secondsToMiliseconds(created)
  const yearInMiliseconds = secondsToMiliseconds(yearToSeconds())
  const yearFromCreatedMiliseconds = createdMiliseconds + yearInMiliseconds
  const end =
    bundle_period === 'year'
      ? milisecondsToDateString(yearFromCreatedMiliseconds)
      : getBillingPeriodEndStringFromSecondsTimestamp(created)
  return {
    start: milisecondsToDateString(createdMiliseconds),
    end,
    trialEnd: trial_end,
    isTrialOngoing: milisecondsToSeconds(nowMilisecondsTimestamp()) < trial_end,
  }
}

/**
 * Helper - find out if timestamp indicates time between two dates
 *
 * @param ts timestamp in miliseconds
 * @param range array of two Date objects
 * @returns a boolean indicating whether the timestamp fits in between the two dates
 */
export const timestampDateInRange = (ts: number, range: [Date, Date]): boolean => {
  const start = range[0]
  start.setHours(0, 0, 0, 0)
  const end = range[1]
  end.setHours(23, 59, 59, 999)
  return start.getTime() <= ts && ts <= end.getTime()
}
